import { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { app, db } from "../utils/firebaseConfig.js";
import { useNavigate } from "react-router-dom";
import AdminHeader from "../Components/AdminHeader.js";
import useFirestoreCollection from "../utils/useFirestoreCollection.js";
import "../Styles/Admin.css";
import Switch from "react-switch";
import WorkingHours from "../Components/WorkingHours.js";
import UpdateWorkingHours from "../Components/UpdateWorkingHours.js";
import ManagePhotos from "../Components/ManagePhotos.js";
import ManageReservations from "../Components/ManageReservations.js";
import { FaPencilAlt } from "react-icons/fa";

const AdminPage = () => {
  // Swap the PWA manifest while the admin page is open, so the
  // "Install app" prompt installs the Admin PWA (not the public site).
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    const original = link.getAttribute("href");
    link.setAttribute("href", "/admin.webmanifest");
    return () => {
      if (original !== null) link.setAttribute("href", original);
    };
  }, []);

  const [editWorkingHours, setEditWorkingHours] = useState(false);
  const [news, setNews] = useState([]);
  const [user, setUser] = useState(null);
  const [isWinter, setIsWinter] = useState(true);
  const [editedNews, setEditedNews] = useState("");
  const [closingDate, setClosingDate] = useState(new Date());
  const [bookings, setBookings] = useState(false);
  const { data: workingHoursData } = useFirestoreCollection("hours");
  const {
    loading: adminLoading,
    error: adminError,
    data: adminData,
  } = useFirestoreCollection("administration"); //change to administration for production or testAdmin for testing

  useEffect(() => {
    if (adminData && adminData.length > 0) {
      setIsWinter(adminData[0].isWinter);
      setNews(adminData[0].news);
      setClosingDate(
        adminData[0].closingDate?.toDate
          ? adminData[0].closingDate.toDate()
          : new Date()
      );
      setBookings(adminData[0].bookings);
    }
  }, [adminData]);

  const navigate = useNavigate();

  useEffect(() => {
    const auth = getAuth(app);
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        // User is authenticated, set the user state
        setUser(user);
      } else {
        // User is not authenticated, redirect to the login page or handle as needed

        // You can use React Router or any other method to navigate to the login page
        setUser(null);
        navigate("/login");
      }
    });

    return () => {
      unsubscribe();
    };
  }, [navigate]);

  const handleEditWorkingHoursToggle = () => {
    setEditWorkingHours((prevEditWorkingHours) => !prevEditWorkingHours);
  };

  const handleEnableBookings = async () => {
    const confirmed = window.confirm("Είστε σίγουροι;");
    if (confirmed) {
      try {
        const docRef = doc(db, "administration", adminData[0].id); // dynamic: uses the loaded doc's own ID, so it works in both testAdmin and administration
        await updateDoc(docRef, {
          bookings: !bookings,
        });
        setBookings(!bookings);
      } catch (error) {
        console.log("Error toggling bookings:", error);
      }
    }
  };

  const handleNewsUpdate = async () => {
    try {
      const docRef = doc(db, "administration", adminData[0].id); // dynamic: uses the loaded doc's own ID, so it works in both testAdmin and administration
      await updateDoc(docRef, {
        news: editedNews,
      });
      setNews(editedNews);
    } catch (error) {
      console.log("Error updating news:", error);
    }
  };

  const handleSeasonalStatusToggle = async () => {
    const confirmed = window.confirm("Είστε σίγουροι;");
    if (confirmed) {
      try {
        const docRef = doc(db, "administration", adminData[0].id); // dynamic: uses the loaded doc's own ID, so it works in both testAdmin and administration
        await updateDoc(docRef, {
          isWinter: !isWinter,
        });
        setIsWinter(!isWinter);
      } catch (error) {
        console.log("Error toggling seasonal status:", error);
      }
    }
  };

  if (!user) {
    return <div>WARNING!!! You are not authenticated.</div>;
  }

  return (
    <div className="admin_page">
      <AdminHeader />

      <div className="admin_top_section">
        {adminLoading ? (
          <p>Loading...</p>
        ) : (
          <div className="admin_card functioning">
            <h3>ΛΕΙΤΟΥΡΓΙΑ</h3>

            <div className="functioning__line">
              <h4 data-open={isWinter ? "false" : "true"}>
                {isWinter ? "ΧΕΙΜΩΝΑΣ — ΚΛΕΙΣΤΑ" : "ΚΑΛΟΚΑΙΡΙ — ΑΝΟΙΚΤΑ"}
              </h4>
              <Switch
                checked={!isWinter}
                onChange={handleSeasonalStatusToggle}
                offColor="#c62828"
                onColor="#2e7d32"
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={48}
              />
            </div>

            <div className="functioning__line">
              <h4 data-open={bookings ? "true" : "false"}>
                {bookings
                  ? "ΚΡΑΤΗΣΕΙΣ ΕΝΕΡΓΕΣ"
                  : "ΚΡΑΤΗΣΕΙΣ ΚΛΕΙΣΤΕΣ"}
              </h4>
              <Switch
                checked={bookings}
                onChange={handleEnableBookings}
                offColor="#c62828"
                onColor="#2e7d32"
                uncheckedIcon={false}
                checkedIcon={false}
                height={22}
                width={48}
              />
            </div>

            {bookings && (
              <div className="closing_date">
                <h4>ΚΡΑΤΗΣΕΙΣ ΜΕΧΡΙ:</h4>
                <p>{closingDate.toLocaleString()}</p>
              </div>
            )}
          </div>
        )}
        <div className="admin_card working-hours">
          <h3>ΩΡΑΡΙΟ</h3>
          {editWorkingHours ? (
            <UpdateWorkingHours
              handleEditWorkingHoursToggle={handleEditWorkingHoursToggle}
            />
          ) : (
            <>
              <WorkingHours />
              <button
                onClick={handleEditWorkingHoursToggle}
                className="edit-button"
              >
                <FaPencilAlt />
                <p>ΕΠΕΞΕΡΓΑΣΙΑ</p>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="admin_card news">
        <h3>ΝΕΑ</h3>
        <input
          type="text"
          value={editedNews}
          placeholder={news}
          onChange={(e) => setEditedNews(e.target.value)}
        />
        <button onClick={handleNewsUpdate} className="edit-button">
          <FaPencilAlt />
          <p>ΑΠΟΘΗΚΕΥΣΗ</p>
        </button>
        <hr />
      </div>

      <div className="admin_card photos">
        <h3>ΦΩΤΟΓΡΑΦΙΕΣ</h3>
        <ManagePhotos />
      </div>

      <div className="admin_card reservations">
        <h3>ΑΙΤΗΜΑΤΑ ΚΡΑΤΗΣΗΣ</h3>
        <ManageReservations />
      </div>
    </div>
  );
};

export default AdminPage;
